import React from 'react';
import {
  CircleCheckIcons,
  ClipBoardIcons,
  ListCheckIcons,
  NewUserIcons,
  ShieldAlertIcons,
  StarIcons,
  UsersIcons,
} from '../../../../../icons/icons';
import { Applicant } from '../../../../../utils/types/JobOpenings';

interface CandidateStatusLegendProps {
  candidates: Applicant[];
}

interface StatusConfig {
  label: string;
  bgColor: string;
  borderColor: string;
  iconColor: string;
  showHeadshot: boolean;
  icon: React.ComponentType<{ className?: string }> | null;
}

type StatusConfigMap = Record<string, StatusConfig>;

// Modern elegant color scheme with light backgrounds and dark borders - using sidebar icons
const STATUS_CONFIG: StatusConfigMap = {
  'Unsuccessful-Pending': {
    label: 'Unsuccessful (Pending)',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-500',
    iconColor: 'text-orange-600',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Unsuccessful-Closed': {
    label: 'Unsuccessful (Closed)',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-500',
    iconColor: 'text-red-600',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Shortlisted': {
    label: 'Shortlisted',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-500',
    iconColor: 'text-emerald-600',
    showHeadshot: false,
    icon: ListCheckIcons,
  },
  'Suitable': {
    label: 'Suitable',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    iconColor: 'text-blue-600',
    showHeadshot: false,
    icon: CircleCheckIcons,
  },
  'Outstanding': {
    label: 'Outstanding',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-500',
    iconColor: 'text-purple-600',
    showHeadshot: false,
    icon: StarIcons,
  },
  'New': {
    label: 'New',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-500',
    iconColor: 'text-cyan-600',
    showHeadshot: false,
    icon: NewUserIcons,
  },
  'Auto-Assessment': {
    label: 'Assessment',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-500',
    iconColor: 'text-amber-600',
    showHeadshot: false,
    icon: ClipBoardIcons,
  },
  'Psychometric': {
    label: 'Psychometric',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-500',
    iconColor: 'text-violet-600',
    showHeadshot: false,
    icon: ListCheckIcons,
  },
  'Wellness': {
    label: 'Wellness',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-500',
    iconColor: 'text-teal-600',
    showHeadshot: false,
    icon: ListCheckIcons,
  },
  'CCA': {
    label: 'CCA',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-500',
    iconColor: 'text-sky-600',
    showHeadshot: false,
    icon: ClipBoardIcons,
  },
  'Interview': {
    label: 'Interview',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-500',
    iconColor: 'text-indigo-600',
    showHeadshot: false,
    icon: UsersIcons,
  },
  'Offer-Extended': {
    label: 'Offer Extended',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    iconColor: 'text-blue-600',
    showHeadshot: false,
    icon: CircleCheckIcons,
  },
  'Offer-Accepted': {
    label: 'Offer Accepted',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-500',
    iconColor: 'text-emerald-600',
    showHeadshot: false,
    icon: CircleCheckIcons,
  },
  'Offer-Declined': {
    label: 'Offer Declined',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-500',
    iconColor: 'text-orange-600',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Offer-Withheld': {
    label: 'Offer Withheld',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-500',
    iconColor: 'text-amber-600',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Waitlisted': {
    label: 'Waitlisted',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-500',
    iconColor: 'text-slate-600',
    showHeadshot: false,
    icon: ListCheckIcons,
  },
  'Not-Suitable': {
    label: 'Not Suitable',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-500',
    iconColor: 'text-rose-600',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Disqualified': {
    label: 'Disqualified',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-500',
    iconColor: 'text-red-600',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Success': {
    label: 'Success',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500',
    iconColor: 'text-green-600',
    showHeadshot: false,
    icon: CircleCheckIcons,
  },
};

const DEFAULT_STATUS_CONFIG: StatusConfig = {
  label: 'Unknown',
  bgColor: 'bg-slate-50',
  borderColor: 'border-slate-400',
  iconColor: 'text-slate-500',
  showHeadshot: false,
  icon: ShieldAlertIcons,
};

// Utility functions
const getStatusConfig = (status: string): StatusConfig => {
  return STATUS_CONFIG[status] || {
    ...DEFAULT_STATUS_CONFIG,
    label: status || 'Unknown',
  };
};

const getStatusCounts = (candidates: Applicant[]): Record<string, number> => {
  const statusCounts: Record<string, number> = {};

  candidates.forEach((candidate) => {
    const status = candidate.applicantStatus || 'Unknown';
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });

  return statusCounts;
};

// Define the desired order of statuses as requested
const STATUS_ORDER = [
  'New',
  'Suitable',
  'Outstanding',
  'Waitlisted',
  'Auto-Assessment',
  'Psychometric',
  'Wellness',
  'CCA',
  'Shortlisted',
  'Interview',
  'Offer-Extended',
  'Offer-Accepted',
  'Offer-Declined',
  'Offer-Withheld',
  'Success',
  'Unsuccessful-Pending',
  'Unsuccessful-Closed',
  'Not-Suitable',
  'Disqualified',
];

// Individual legend item component with modern elegant styling
const LegendItem: React.FC<{
  count: number;
  config: StatusConfig;
}> = ({ count, config }) => {
  const {
    label,
    bgColor,
    borderColor,
    iconColor,
    showHeadshot,
    icon: IconComponent,
  } = config;

  return (
    <div className='flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-1 shadow-sm'>
      <div
          className={`w-5 h-5 ${bgColor} ${borderColor} rounded-full border-2 flex items-center justify-center shadow-sm transition-all duration-200 hover:scale-105`}
      >
        { showHeadshot ? (
          <UsersIcons className={`w-2.5 h-2.5 ${iconColor} stroke-2`} />
        ) : (
          IconComponent && (
            <IconComponent className={`w-2.5 h-2.5 ${iconColor} stroke-2`} />
          )
        ) }
      </div>
      <span className='whitespace-nowrap text-[11px] font-medium text-slate-700'>
        { label } <span className='text-slate-500'>({ count })</span>
      </span>
    </div>
  );
};

// Main legend component with modern elegant styling
export const CandidateStatusLegend: React.FC<CandidateStatusLegendProps> = ({
  candidates,
}) => {
  const statusCounts = getStatusCounts(candidates);

  // Sort statuses according to the specified order
  const sortedStatuses = STATUS_ORDER.filter(
    (status) => statusCounts[status] > 0,
  ) // Only show statuses that have candidates
    .concat(
      // Add any statuses not in the predefined order at the end
      Object.keys(statusCounts).filter(
        (status) => !STATUS_ORDER.includes(status),
      ),
    );

  return (
    <div className='rounded-lg border border-slate-200/70 bg-gradient-to-r from-slate-50 to-gray-50 px-1.5 py-1.5'>
      <div className='overflow-x-auto'>
        <div className='flex min-w-max items-center gap-1.5 text-xs'>
          <span className='flex shrink-0 items-center gap-1.5 rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200'>
            <div className='w-1.5 h-1.5 bg-blue-500 rounded-full'></div>
            Candidate States:
          </span>

          { sortedStatuses.map((status) => (
            <LegendItem
                key={status}
                count={statusCounts[status]}
                config={getStatusConfig(status)}
            />
          )) }
        </div>
      </div>
    </div>
  );
};
